<template>
  <div class="px-6 py-6 overflow-y-auto">
    <h1 class="text-2xl font-semibold text-gray-800">Overview</h1>
    <div class="mt-2 grid grid-cols-4 gap-6">
      <div class="bg-white shadow rounded-md overflow-hidden flex flex-col">
        <div class="flex-grow px-4 py-4 flex items-center space-x-6">
          <UsersIcon class="h-6 w-6 text-gray-400" />
          <div>
            <h2 class="text-base font-medium text-gray-500">
              จำนวนลูกค้าทั้งหมด
            </h2>
            <p class="text-xl font-semibold text-gray-800">
              {{ customersAll }} ราย
            </p>
          </div>
        </div>
        <div class="px-4 py-2 bg-gray-50 text-base font-medium text-cyan-600">
          <button
            type="button"
            class="font-medium"
            @click="
              handleCustomerDetails({
                type: 'all',
                title: 'จำนวนลูกค้าทั้งหมด',
              })
            "
          >
            ดูรายละเอียด
          </button>
        </div>
      </div>

      <div class="bg-white shadow rounded-md overflow-hidden flex flex-col">
        <div class="flex-grow px-4 py-4 flex items-center space-x-6">
          <ActiveUsersIcon class="h-6 w-6 text-gray-400" />
          <div>
            <h2 class="text-base font-medium text-gray-500">
              จำนวนลูกค้าที่ใช้งาน
            </h2>
            <p class="text-xl font-semibold text-gray-800">
              {{ customerUseActive }} ราย
            </p>
          </div>
        </div>
        <div class="px-4 py-2 bg-gray-50 text-base font-medium text-cyan-600">
          <button
            type="button"
            class="font-medium"
            @click="
              handleCustomerDetails({
                type: 'use',
                title: 'จำนวนลูกค้าที่ใช้งาน',
              })
            "
          >
            ดูรายละเอียด
          </button>
        </div>
      </div>

      <div class="bg-white shadow rounded-md overflow-hidden flex flex-col">
        <div class="flex-grow px-4 py-4 flex items-center space-x-6">
          <ClockIcon class="h-6 w-6 text-gray-400" />
          <div>
            <h2 class="text-base font-medium text-gray-500">
              ลูกค้าใกล้หมดอายุใช้งาน
            </h2>
            <p class="text-xl font-semibold text-yellow-400">
              {{ customerUseNearExpiration }} ราย
            </p>
          </div>
        </div>
        <div class="px-4 py-2 bg-gray-50 text-base font-medium text-cyan-600">
          <button
            type="button"
            class="font-medium"
            @click="
              handleCustomerDetails({
                type: 'nearExpiration',
                title: 'ลูกค้าใกล้หมดอายุใช้งาน',
              })
            "
          >
            ดูรายละเอียด
          </button>
        </div>
      </div>

      <div class="bg-white shadow rounded-md overflow-hidden flex flex-col">
        <div class="flex-grow px-4 py-4 flex items-center space-x-6">
          <SadFaceIcon class="h-6 w-6 text-gray-400" />
          <div>
            <h2 class="text-base font-medium text-gray-500">
              ลูกค้าหมดอายุการใช้งาน
            </h2>
            <p class="text-xl font-semibold text-red-600">
              {{ customerUseExpire }} ราย
            </p>
          </div>
        </div>
        <div class="px-4 py-2 bg-gray-50 text-base font-medium text-cyan-600">
          <button
            type="button"
            class="font-medium"
            @click="
              handleCustomerDetails({
                type: 'expire',
                title: 'ลูกค้าหมดอายุการใช้งาน',
              })
            "
          >
            ดูรายละเอียด
          </button>
        </div>
      </div>
    </div>

    <div class="mt-6 flex items-end justify-between">
      <h2 class="text-2xl font-semibold text-gray-800">
        {{ customerDetails.title }} {{ filterCustomers.total }} ราย
      </h2>
      <div>
        <SearchWidgetResultsList v-model="search" />
      </div>
    </div>

    <!-- table -->
    <div class="mt-4">
      <AppDataTable
        :isCheckbox="false"
        :options="options"
        :headers="tableHeaders"
        :items="filterCustomers.items"
        :loading="loading"
        @sort="sort"
      >
        <template v-slot:title_th="{ item }">
          <button
            @click="
              $router.push({
                name: 'CustomerDetails',
                params: { uuid: item.company_uuid },
              })
            "
            type="button"
            class="text-cyan-600 font-medium hover:underline text-left"
          >
            {{ item.title_th }}
          </button>
        </template>
        <template v-slot:activate_start_date="{ item }">
          {{ item.activate_start_date | formatDate }}
        </template>
        <template v-slot:activate_end_date="{ item }">
          <p
            :class="
              customerDetails.type === 'expire' && 'text-red-600 font-medium'
            "
          >
            {{ item.activate_end_date | formatDate }}
          </p>
          <p
            v-if="customerDetails.type === 'nearExpiration'"
            class="text-xs font-medium text-yellow-600"
          >
            ({{ getDaysRemaining(item.activate_end_date) }})
          </p>
        </template>
        <template v-slot:pre_expiration_notification_sent_at="{ item }">
          <div>
            <p>
              {{ item.pre_expiration_notification_sent_at | formatDate }}
            </p>
            <p
              v-if="item.pre_expiration_notification_sent_at"
              class="text-xs font-medium text-cyan-600"
            >
              ({{ getDaysAgo(item.pre_expiration_notification_sent_at) }})
            </p>
          </div>
        </template>
        <template v-slot:send_email="{ item }">
          <p v-if="isEmailAlreadySent(item)" class="text-sm text-cyan-600">
            ส่งอีเมลแล้ว
          </p>
          <button
            v-else
            @click="handleSendEmail(item)"
            :disabled="sendingEmailFor === item.company_uuid"
            :class="{ 'cursor-not-allowed': loading }"
            type="button"
            class="
              border
              px-2
              py-1
              rounded-md
              text-sm text-gray-600
              hover:text-gray-900
            "
          >
            <div class="flex items-center">
              <svg
                v-if="sendingEmailFor === item.company_uuid"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-cyan-700"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span> SEND EMAIL </span>
            </div>
          </button>
        </template>
      </AppDataTable>
    </div>
    <!-- end table -->

    <!-- pagination -->
    <div class="py-4 flex items-center justify-start">
      <AppPagination
        :options="options"
        :startNumberItem="startNumberItem"
        :endNumberItem="endNumberItem"
        :totalItems="filterCustomers.total"
        :isPrevPage="isPrevPage"
        :isNextPage="isNextPage"
        @decreasePage="() => options.page--"
        @increasePage="() => options.page++"
        @itemsPerPageChange="() => (options.page = 1)"
      />
    </div>
    <!-- end pagination -->

    <ModalCustomerAboutToExpire />
  </div>
</template>

<script>
// @ is an alias to /src
import AppDataTable from '@/components/AppDataTable'
import AppPagination from '@/components/AppPagination'
import ActiveUsersIcon from '@/components/icons/ActiveUsersIcon.vue'
import ClockIcon from '@/components/icons/ClockIcon.vue'
import SadFaceIcon from '@/components/icons/SadFaceIcon.vue'
import UsersIcon from '@/components/icons/UsersIcon.vue'
import SearchWidgetResultsList from '@/components/SearchWidgetResultsList'
import { DASHBOARD_CONSTANTS } from '@/constants/dashboard'
import { debounce } from 'lodash'
import moment from 'moment'

import ModalCustomerAboutToExpire from '@/components/ModalCustomerAboutToExpire.vue'

export default {
  name: 'Dashboard',

  components: {
    AppDataTable,
    AppPagination,
    ModalCustomerAboutToExpire,
    SearchWidgetResultsList,
    UsersIcon,
    ActiveUsersIcon,
    ClockIcon,
    SadFaceIcon,
  },

  data() {
    return {
      now: moment.utc().format('YYYY-MM-DD'),
      loading: false,
      sendingEmailFor: null,
      search: '',
      debouncedSearch: '',
      options: {
        sortBy: 'company_code',
        sortDesc: false,
        itemsPerPage: DASHBOARD_CONSTANTS.DEFAULT_ITEMS_PER_PAGE,
        page: 1,
      },
      customerDetails: {
        type: 'all',
        title: 'จำนวนลูกค้าทั้งหมด',
      },
    }
  },

  filters: {
    formatDate: function (value) {
      return value ? moment(String(value)).format('DD/MM/YYYY') : '-'
    },
  },

  watch: {
    search: debounce(function (newValue) {
      this.debouncedSearch = newValue
      this.options.page = 1
    }, 300),
  },

  computed: {
    isEmailAlreadySent() {
      return (item) => {
        if (!item.pre_expiration_notification_sent_at) {
          return false // ยังไม่เคยส่ง → ส่งได้
        }

        const today = moment.utc().startOf('day')
        const expiryDate = moment.utc(item.activate_end_date).startOf('day')
        const sentAt = moment
          .utc(item.pre_expiration_notification_sent_at)
          .startOf('day')

        const daysUntilExpiry = expiryDate.diff(today, 'days')

        // เหลือมากกว่า 7 วัน → ส่งไปแล้ว ไม่สามารถส่งได้อีก
        if (daysUntilExpiry > 7) {
          return true
        }

        // เหลือ 7 วันหรือน้อยกว่า → ส่งได้วันละ 1 ครั้ง (ต้องเป็นคนละวัน)
        const daysSinceSent = today.diff(sentAt, 'days')
        return daysSinceSent < 1
      }
    },

    tableHeaders() {
      if (this.customerDetails.type === 'nearExpiration') {
        return [
          {
            text: 'รหัสลูกค้า',
            class: 'text-left w-28',
            value: 'company_code',
          },
          { text: 'ชื่อลูกค้า (ไทย)', class: 'text-left', value: 'title_th' },
          { text: 'ที่อยู่ (ไทย)', class: 'text-left', value: 'address_th' },
          {
            text: 'สิ้นสุดการใช้งาน',
            class: 'text-left w-40',
            value: 'activate_end_date',
          },
          {
            text: 'ส่งอีเมลแจ้งเตือนล่าสุด',
            class: 'text-left w-48',
            value: 'pre_expiration_notification_sent_at',
          },
          {
            text: 'ส่งอีเมล',
            sortable: false,
            class: 'text-left w-40',
            value: 'send_email',
          },
        ]
      }
      return [
        { text: 'รหัสลูกค้า', class: 'text-left w-28', value: 'company_code' },
        { text: 'ชื่อลูกค้า (ไทย)', class: 'text-left', value: 'title_th' },
        { text: 'ที่อยู่ (ไทย)', class: 'text-left', value: 'address_th' },
        {
          text: 'เริ่มต้นใช้งาน',
          class: 'text-left w-40',
          value: 'activate_start_date',
        },
        {
          text: 'สิ้นสุดการใช้งาน',
          class: 'text-left w-40',
          value: 'activate_end_date',
        },
      ]
    },

    oneMonthFromNow() {
      return moment
        .utc(new Date())
        .add(DASHBOARD_CONSTANTS.EXPIRY_WARNING_MONTHS, 'M')
        .format('YYYY-MM-DD')
    },

    categorizedCustomers() {
      return {
        all: this.customers,
        active: this.customers.filter(this.isActiveCustomer),
        nearExpiration: this.customers.filter(this.isNearExpiration),
        expired: this.customers.filter(this.isExpired),
      }
    },

    customerUseNearExpiration() {
      return this.categorizedCustomers.nearExpiration.length
    },

    customerUseActive() {
      return this.categorizedCustomers.active.length
    },

    customerUseExpire() {
      return this.categorizedCustomers.expired.length
    },

    customersAll() {
      return this.$store.state.customer.customers.length
    },

    customers() {
      return this.$store.state.customer.customers
    },

    filterCustomers() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let items = this.customers

      if (this.customerDetails.type === 'use') {
        items = items.filter(this.isActiveCustomer)
      }

      if (this.customerDetails.type === 'nearExpiration') {
        items = items.filter(this.isNearExpiration)
      }

      if (this.customerDetails.type === 'expire') {
        items = items.filter(this.isExpired)
      }

      if (this.debouncedSearch) {
        items = items.filter((item) =>
          this.matchesSearchQuery(item, this.debouncedSearch)
        )
      }

      items = items.sort((a, b) => {
        const sortA = a[sortBy] || ''
        const sortB = b[sortBy] || ''

        if (sortDesc) {
          if (sortA < sortB) return 1
          if (sortA > sortB) return -1
          return 0
        } else {
          if (sortA < sortB) return -1
          if (sortA > sortB) return 1
          return 0
        }
      })

      const total = items.length

      if (itemsPerPage > 0) {
        items = items.slice((page - 1) * itemsPerPage, page * itemsPerPage)
      }

      return { items, total }
    },

    startNumberItem() {
      if (this.filterCustomers.total === 0) return 0

      return (this.options.page - 1) * this.options.itemsPerPage + 1
    },

    endNumberItem() {
      const sum = this.options.page * this.options.itemsPerPage
      if (sum < this.filterCustomers.total) return sum

      return this.filterCustomers.total
    },

    isPrevPage() {
      if (this.options.page === 1) return true

      return false
    },

    isNextPage() {
      if (this.filterCustomers.total === 0) return true

      return (
        this.options.page ===
        Math.ceil(this.filterCustomers.total / this.options.itemsPerPage)
      )
    },
  },

  created() {
    this.$store.commit('setMenusCustomerActive', false)
    this.$store.commit('setBreadcrumbs', [])

    // refresh every hour
    this._timer = setInterval(() => {
      this.now = moment.utc().format('YYYY-MM-DD')
    }, 60 * 60 * 1000)

    this.fetchDataFromApi()
  },

  methods: {
    getDaysAgo(date) {
      if (!date) return ''
      const today = moment.utc().startOf('day')
      const inputDate = moment.utc(date).startOf('day')
      const days = today.diff(inputDate, 'days')
      if (days === 0) return 'วันนี้'
      if (days >= 1) return `${days} วันที่แล้ว`
      return ''
    },

    getDaysRemaining(date) {
      if (!date) return ''
      const expiryDate = moment.utc(date).endOf('day')
      const now = moment.utc()
      const days = expiryDate.diff(now, 'days')
      if (days < 0) return 'หมดอายุแล้ว'
      if (days === 0) return 'สิ้นสุดวันนี้'
      return `เหลืออีก ${days} วัน`
    },

    async handleSendEmail(item) {
      try {
        this.sendingEmailFor = item.company_uuid
        await this.$store.dispatch('customer/sendEmailToCompanyAboutToExpire', {
          company_uuid: item.company_uuid,
          body: {},
        })

        await this.$store.dispatch('customer/fetchCustomers')
      } catch (error) {
        console.error('Error sending email:', error)
        this.$store.commit('notification/add', {
          type: 'error',
          title: 'ส่งอีเมลไม่สำเร็จ',
          message: 'เกิดข้อผิดพลาดในการส่งอีเมล กรุณาลองใหม่อีกครั้ง',
        })
      } finally {
        this.sendingEmailFor = null
      }
    },

    getSearchableFields(item) {
      return {
        company_code: item.company_code,
        title_th: item.title_th,
        address_th: item.address_th || '',
        activate_start_date: this.formatDateForSearch(item.activate_start_date),
        activate_end_date: this.formatDateForSearch(item.activate_end_date),
      }
    },

    formatDateForSearch(date) {
      return date ? moment(date).format('DD/MM/YYYY') : ''
    },

    matchesSearchQuery(item, query) {
      const searchableValues = Object.values(this.getSearchableFields(item))
      const lowerQuery = query.toLowerCase()
      return searchableValues.some((value) =>
        value.toString().toLowerCase().includes(lowerQuery)
      )
    },

    isActiveCustomer(customer) {
      return (
        !customer.activate_end_date || customer.activate_end_date >= this.now
      )
    },

    isNearExpiration(customer) {
      return (
        customer.activate_end_date >= this.now &&
        customer.activate_end_date <= this.oneMonthFromNow
      )
    },

    isExpired(customer) {
      return customer.activate_end_date && customer.activate_end_date < this.now
    },

    handleCustomerDetails(valueObj) {
      this.customerDetails = { ...valueObj }
      this.options.page = 1
    },

    sort(value) {
      if (this.options.sortBy === value) {
        this.options.sortDesc = !this.options.sortDesc
        this.options.page = 1
      } else {
        this.options.sortBy = value
        this.options.sortDesc = false
        this.options.page = 1
      }
    },

    async fetchDataFromApi() {
      try {
        this.loading = true
        await this.$store.dispatch('customer/fetchCustomers')

        const needsNotification = this.categorizedCustomers.nearExpiration.some(
          (item) => !this.isEmailAlreadySent(item)
        )

        if (needsNotification) {
          this.$store.commit('setShowModalCustomerAboutToExpire', true)
        }
      } catch (error) {
        console.error('Failed to fetch customers:', error)

        this.$store.commit('notification/add', {
          type: 'error',
          title: 'เกิดข้อผิดพลาด',
          message: 'ไม่สามารถโหลดข้อมูลลูกค้าได้ กรุณาลองใหม่อีกครั้ง',
        })
      } finally {
        this.loading = false
      }
    },
  },
  beforeDestroy() {
    clearInterval(this._timer)
  },
}
</script>
